/**
 * Sidebar Component
 * Handles Sidebar navigation, Folder Tree, and Machine switching.
 */

import { loadConfigAsync } from '../utils/config.js';
import { loadConfig, saveConfig, applyTheme, getMachines, getActiveMachine, setActiveMachine, getFavoritesForMachine, removeFavorite, normalizeConfig } from '../utils/config.js';
import { listDirectory } from '../api/fs.js';
import { apiUpload } from '../api/client.js';
import { debug } from '../utils/debug.js';
import { openTab, closeAllToolTabs, hasDirtyToolState } from './tab-manager.js';
import { showToast, showConfirmDialog } from '../utils/ui.js';
import { ensureAuthenticatedMachine } from '../app.js';
import { getSysStats } from '../api/sys.js';

export async function initSidebar() {
    debug.log('Initializing Sidebar...');
    const config = await loadConfigAsync();

    renderFavourites();
    initFolderTree();
    initUtilities();
    initSettingsOverlay(config);
    renderMachines();
    initLogoutMobileClose();
    initSysStats();

    // Re-render sidebar when machine changes (skip if sidebar triggered it)
    window.addEventListener('machine-changed', (e) => {
        if (e.detail?.source === 'sidebar') return;
        refreshSidebarState();
    });

    // Re-render favorites when changed from file browser or elsewhere
    window.addEventListener('favorites-changed', () => {
        renderFavourites();
    });
}

/**
 * Refresh sidebar state (favorites, folder tree, machines).
 * Called after machine switch or logout fallback.
 */
export async function refreshSidebarState() {
    renderFavourites();
    await initFolderTree();
    renderMachines();
}

/**
 * Close sidebar on mobile viewports (≤800px).
 * No-op on desktop where sidebar is always visible.
 */
function closeSidebarMobile() {
    if (window.innerWidth > 800) return;
    document.documentElement.classList.add('sidebar-collapsed');
}

/**
 * Wire logout button to close sidebar on mobile.
 */
function initLogoutMobileClose() {
    const logoutBtn = Array.from(document.querySelectorAll('.nav-item')).find(item => item.title === 'Logout');
    logoutBtn?.addEventListener('click', () => closeSidebarMobile());
}

/**
 * Render Favorites Section
 */
function renderFavourites() {
    const container = document.querySelector('#sidebar-favorites');
    if (!container) return;

    const activeMachine = getActiveMachine();
    const favorites = getFavoritesForMachine(activeMachine?.name || 'Self');

    // Clear existing items (keep title)
    const title = container.querySelector('.nav-section-title');
    container.innerHTML = '';
    if (title) container.appendChild(title);

    favorites.forEach(fav => {
        const item = document.createElement('div');
        item.className = 'nav-item';
        item.tabIndex = 0;
        item.innerHTML = `
            <span class="icon">${fav.icon || 'folder'}</span>
            <span class="text">${fav.label}</span>
            <span class="icon icon-sm pin-button pinned">kid_star</span>
        `;
        item.addEventListener('click', (e) => {
            // pin-button has its own handler; ignore clicks that bubbled from it
            if (e.target.closest('.pin-button')) return;
            navigateTo(fav.path);
            closeSidebarMobile();
        });

        // Star icon click -> unpin (stopPropagation to avoid navigation)
        const pinBtn = item.querySelector('.pin-button');
        pinBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const machineName = activeMachine?.name || 'Self';
            removeFavorite(machineName, fav.path);
        });

        container.appendChild(item);
    });
}

/**
 * Initialize Folder Tree
 */

async function initFolderTree() {
    const container = document.querySelector('.folder-tree');
    if (!container) return;
    
    container.innerHTML = ''; // Clear static content, if any
    
    // Create root item
    const rootUL = document.createElement('ul');
    container.appendChild(rootUL);

    const rootDir = window.appState.rootDir || '/';
    const rootItem = createTreeItem('/', rootDir, true);
    rootUL.appendChild(rootItem);
    
    // Auto-expand root only on screens 950px or taller
    if (window.innerHeight >= 950) {
        await toggleTreeItem(rootItem, rootDir, true);
    }
}

/**
 * Create a tree item element
 * @param {string} name - Display name
 * @param {string} path - Full path
 * @param {boolean} isDir - Is directory
 */
function createTreeItem(name, path, isDir) {
    if (!isDir) return null; // We only show folders in tree
    
    const tree_node = document.createElement('li');

    tree_node.innerHTML = `
        <button>
            <span class="icon icon-sm chevron">keyboard_arrow_right</span>
            <span class="text">${name}</span>
        </button>
    `;
    
    tree_node.dataset.path = path;
    
    const childContainer = document.createElement('ul');
    childContainer.className = 'tree-children';
    childContainer.style.display = 'none';

    tree_node.appendChild(childContainer);

    // Click on row -> Navigate
    tree_node.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // If clicking chevron, toggle expand
        // Separate Listener for chevron if needed, but closest check handles it.
        if (e.target.closest('.chevron')) {
            toggleTreeItem(tree_node, path);
        } else {
            // Navigate
            navigateTo(path);
            closeSidebarMobile();
            // Tree structure (expand/collapse) solely controlled by chevron click
            // Click on tree-item row (outside chevron), just lead the workspace-file nav to it
        }
    });
    
    return tree_node;
}

/**
 * Toggle tree item expansion
 * @param {HTMLElement} tree_node - The wrapper element containing button and ul
 * @param {string} path - Path to list
 * @param {boolean} forceExpand - If true, force expand
 */
async function toggleTreeItem(tree_node, path, forceExpand = false) {
    const childContainer = tree_node.querySelector('.tree-children');
    const chevron = tree_node.querySelector('.chevron');
    
    const isExpanded = childContainer.style.display !== 'none';
    
    if (isExpanded && !forceExpand) {
        // Collapse
        childContainer.style.display = 'none';
        chevron.innerText = 'keyboard_arrow_right';
        return;
    }
    
    // Expand
    childContainer.style.display = 'block';
    chevron.innerText = 'keyboard_arrow_down';
    
    // Load children if empty
    if (childContainer.children.length === 0) {
        try {
            const items = await listDirectory(path);
            // Sort: folders first, then name
            items.sort((a, b) => {
                if (a.is_dir !== b.is_dir) return b.is_dir ? 1 : -1;
                return a.name.localeCompare(b.name);
            });
            
            items.forEach(item => {
                if (item.is_dir) {
                    const childPath = path === '/' ? `/${item.name}` : `${path}/${item.name}`;
                    const childNode = createTreeItem(item.name, childPath, true);
                    childContainer.appendChild(childNode);
                }
            });
        } catch (err) {
            console.error('Tree load failed:', err);
            // Later to change error handling with toast message with error details, e.g. no permission
            childContainer.innerHTML = `<li><button>
                                            <span class="icon icon-sm chevron">exclamation</span>
                                            <span class="text">Error Loading</span>
                                        </button></li>`;
        }
    }
}

/**
 * Navigate workspace to path
 */
function navigateTo(path) {
    const fileBrowser = document.getElementById('main-file-browser') || document.querySelector('file-browser-logic');
    if (fileBrowser) {
        fileBrowser.currentPath = path;
    }
    // Switch to files tab if not active
    const tabs = document.querySelectorAll('.tab-item');
    const filesTab = Array.from(tabs).find(t => t.querySelector('.tab-text')?.innerText === 'Files');
    if (filesTab && !filesTab.classList.contains('active')) {
        filesTab.click();
    }
}

/**
 * Initialize Utilities (Terminal/Editor)
 */
function initUtilities() {
    const utils = document.querySelectorAll('#sidebar-utilities .nav-item');
    utils.forEach(item => {
        item.addEventListener('click', async () => {
            const action = item.dataset.action;
            closeSidebarMobile();
            if (action === 'terminal') {
                openTab('terminal');
            } else if (action === 'editor') {
                // Generate timestamped filename
                const now = new Date();
                const pad = (n) => String(n).padStart(2, '0');
                const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
                const filename = `Untitled_${timestamp}.md`;
                const filePath = `./tmp/${filename}`;

                try {
                    // Upload empty file to create it on the backend
                    const emptyBlob = new File([''], filename, { type: 'text/plain; charset=utf-8' });
                    await apiUpload('./tmp', [{ file: emptyBlob, name: filename }]);
                    openTab('editor', { label: filename, path: filePath, isNew: true });
                } catch (err) {
                    // Fallback: open in-memory blank editor
                    showToast('Could not create temp file, opening in-memory editor', 'warning');
                    openTab('editor', { label: filename });
                }
            }
        });
    });
}

/**
 * Initialize System Stats widget with polling.
 * Fetches CPU, RAM and Disk usage every 10 seconds and updates the sidebar bars.
 */
function initSysStats() {
    const cpuBar  = document.getElementById('stats-cpu-bar');
    const cpuText = document.getElementById('stats-cpu-text');
    const ramBar  = document.getElementById('stats-ram-bar');
    const ramText = document.getElementById('stats-ram-text');
    const diskBar  = document.getElementById('stats-disk-bar');
    const diskText = document.getElementById('stats-disk-text');

    if (!ramBar || !diskBar || !cpuBar) return;

    function fmtBytes(bytes) {
        if (bytes >= 1099511627776) return (bytes / 1099511627776).toFixed(1) + 'TB';
        if (bytes >= 1073741824)    return (bytes / 1073741824).toFixed(1) + 'GB';
        if (bytes >= 1048576)       return (bytes / 1048576).toFixed(0) + 'MB';
        return (bytes / 1024).toFixed(0) + 'KB';
    }

    async function refresh() {
        try {
            const s = await getSysStats();

            const cpuPct  = s.cpu_usage !== undefined ? Math.round(s.cpu_usage) : 0;
            const ramPct  = s.ram_total  > 0 ? Math.round(s.ram_used  / s.ram_total  * 100) : 0;
            const diskPct = s.disk_total > 0 ? Math.round(s.disk_used / s.disk_total * 100) : 0;

            cpuBar.style.width  = cpuPct  + '%';
            ramBar.style.width  = ramPct  + '%';
            diskBar.style.width = diskPct + '%';

            // Colour the bar red when usage > 85%
            cpuBar.classList.toggle('stats-bar-warn',  cpuPct  > 85);
            ramBar.classList.toggle('stats-bar-warn',  ramPct  > 85);
            diskBar.classList.toggle('stats-bar-warn', diskPct > 85);

            cpuText.textContent  = `${cpuPct}%`;
            ramText.textContent  = `${fmtBytes(s.ram_used)} / ${fmtBytes(s.ram_total)}`;
            diskText.textContent = `${fmtBytes(s.disk_used)} / ${fmtBytes(s.disk_total)}`;
        } catch {
            cpuText.textContent  = 'N/A';
            ramText.textContent  = 'N/A';
            diskText.textContent = 'N/A';
        }
    }

    refresh();
    setInterval(refresh, 10000);
    
    // Refresh immediately when the active machine is changed
    window.addEventListener('machine-changed', refresh);
}

/**
 * Initialize Settings Overlay
 */
function initSettingsOverlay(config) {
    const overlay = document.getElementById('setting-overlay');
    const openBtn = document.getElementById('btn-settings');
    const cancelBtn = overlay?.querySelector('.btn-secondary');
    const saveBtn = overlay?.querySelector('.btn-primary');
    const closeBtn = overlay?.querySelector('.settings-close');

    if (!overlay || !openBtn) return;

    const closeModal = () => { overlay.style.display = 'none'; };

    openBtn.addEventListener('click', () => {
        closeSidebarMobile();
        overlay.style.display = 'flex';
        populateSettings(config);
    });

    cancelBtn?.addEventListener('click', closeModal);
    closeBtn?.addEventListener('click', closeModal);

    // Close on overlay background click
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeModal();
    });

    // Add Machine button — appended after the list items inside .settings-list
    const addBtn = overlay.querySelector('.settings-add-btn');
    addBtn?.addEventListener('click', () => {
        const list = overlay.querySelector('.settings-list');
        if (list) {
            // Insert before "Add Machine" button
            list.insertBefore(createMachineItem({ url: '', name: '' }), addBtn);
        }
    });

    saveBtn?.addEventListener('click', () => {
        const formData = normalizeConfig(gatherSettingsData());
        const saved = saveConfig(formData);
        if (saved) {
            const nextConfig = loadConfig();
            window.appState.config = nextConfig;
            renderMachines();
            showToast('Settings saved', 'info');
            closeModal();
        }
    });
}

/**
 * Populate settings form with current config (machines only)
 */
function populateSettings(config) {
    const list = document.querySelector('#setting-overlay .settings-list');
    if (!list) return;

    // Clear everything except the Add button
    list.querySelectorAll('.settings-item').forEach(el => el.remove());

    const addBtn = list.querySelector('.settings-add-btn');
    config.machines.filter(m => !m.isSelf).forEach(machine => {
        const item = createMachineItem(machine);
        if (addBtn) {
            list.insertBefore(item, addBtn);
        } else {
            list.appendChild(item);
        }
    });

    // Ensure Add Machine button exists (create once if missing)
    if (!addBtn) {
        const btn = document.createElement('button');
        btn.className = 'settings-add-btn';
        btn.innerHTML = '<span class="icon">add</span> Add Machine';
        list.appendChild(btn);
    }
}

/**
 * Create a machine item for settings (click-to-edit readonly inputs)
 */
function createMachineItem(machine) {
    const div = document.createElement('div');
    div.className = 'settings-item';

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'settings-input';
    nameInput.placeholder = 'Name';
    nameInput.value = machine.name || '';
    nameInput.readOnly = true;

    const urlInput = document.createElement('input');
    urlInput.type = 'text';
    urlInput.className = 'settings-input';
    urlInput.placeholder = 'URL (e.g. http://host:3000)';
    urlInput.value = machine.url || '';
    urlInput.readOnly = true;

    // Click-to-edit: remove readonly on focus, restore on blur
    [nameInput, urlInput].forEach(input => {
        input.addEventListener('focus', () => { input.readOnly = false; });
        input.addEventListener('blur', () => { input.readOnly = true; });
    });

    const delBtn = document.createElement('button');
    delBtn.className = 'settings-delete';
    delBtn.innerHTML = '<span class="icon icon-sm">delete</span>';
    delBtn.addEventListener('click', () => div.remove());

    div.append(nameInput, urlInput, delBtn);
    return div;
}

/**
 * Gather machines data from settings form
 */
function gatherSettingsData() {
    const config = loadConfig();
    const overlay = document.getElementById('setting-overlay');

    const machineItems = overlay?.querySelectorAll('.settings-item') || [];
    const machines = Array.from(machineItems).map(item => {
        const inputs = item.querySelectorAll('.settings-input');
        return {
            name: inputs[0]?.value?.trim() || '',
            url: inputs[1]?.value?.trim() || ''
        };
    }).filter(m => m.name && m.url);

    return { ...config, machines };
}

/**
 * Render Sidebar Machines Menu
 */
let switchingMachine = false; // Guard flag to prevent concurrent switch attempts

function renderMachines() {
    const config = loadConfig();
    const machines = getMachines();
    const activeMachine = getActiveMachine();
    const container = document.querySelector('#sidebar-machines');
    const menu = container?.querySelector('.account-menu');
    const expandIcon = container?.querySelector('.expand-icon');
    const labelEl = container?.querySelector('.machine-label');
    
    if (!container || !menu) return;

    if (labelEl) {
        labelEl.textContent = activeMachine?.name || 'Machine';
    }

    // as the content sits at the left of the chevron, and expands above the container, use left arrow and up arrow.
    const closeMenu = () => {
        container.classList.remove('active');
        if (expandIcon) expandIcon.innerText = 'keyboard_arrow_left';
        menu.style.display = 'none';
    };

    const openMenu = () => {
        container.classList.add('active');
        if (expandIcon) expandIcon.innerText = 'keyboard_arrow_up';
        menu.style.display = 'block';
    };

    // Toggle Menu (bind once)
    if (!container.dataset.menuToggleBound) {
        container.addEventListener('click', (e) => {
            if (e.target.closest('.account-menu')) return;

            if (container.classList.contains('active')) {
                closeMenu();
            } else {
                openMenu();
            }
        });

        // Outside-click dismiss for the machine menu
        document.addEventListener('click', (e) => {
            if (!container.classList.contains('active')) return;
            if (!container.contains(e.target)) {
                closeMenu();
            }
        });

        container.dataset.menuToggleBound = 'true';
    }

    // Populate Menu
    menu.innerHTML = '';
    machines.forEach((machine, index) => {
        const item = document.createElement('div');
        const isActive = machine.name === (window.appState?.activeMachine ?? 'Self');
        item.className = 'menu-item';
        item.innerHTML = `
            ${machine.name}
            ${isActive ? '<span class="icon icon-sm check">check</span>' : ''}
        `;

        item.addEventListener('click', async () => {
            // Clicking active machine is a no-op — just close menu
            if (isActive) {
                closeMenu();
                return;
            }

            // Guard against concurrent switches
            if (switchingMachine) return;
            switchingMachine = true;

            try {
                // Check for dirty state
                const { hasDirtyEditors, hasTerminals } = hasDirtyToolState();
                if (hasDirtyEditors || hasTerminals) {
                    const warnings = [];
                    if (hasDirtyEditors) warnings.push('unsaved editor changes');
                    if (hasTerminals) warnings.push('active terminal connections');
                    const confirmed = await showConfirmDialog(
                        'Switch Machine?',
                        `You have ${warnings.join(' and ')}. Switching will close all tool tabs. Continue?`
                    );
                    if (!confirmed) {
                        closeMenu();
                        return;
                    }
                }

                debug.log(`Switching to machine: ${machine.name}`);

                // Auth-check the TARGET machine BEFORE any state changes
                const authOk = await ensureAuthenticatedMachine(machine, { required: false });
                if (!authOk) {
                    debug.log(`Auth failed/aborted for ${machine.name} — switch cancelled`);
                    closeMenu();
                    return;
                }

                // Auth succeeded — now commit the switch
                setActiveMachine(machine.name);
                const nextConfig = loadConfig();
                window.appState.config = nextConfig;

                // Close all tool tabs (terminal/editor)
                closeAllToolTabs();

                // Reset file browser to root and force reload
                const browser = document.getElementById('main-file-browser');
                if (browser) {
                    const targetPath = window.appState.rootDir || '/';
                    if (browser.currentPath === targetPath) {
                        // Path already root — Lit won't detect a change, force reload
                        browser.reload();
                    } else {
                        // Path changes — Lit's updated() will handle the refresh
                        browser.currentPath = targetPath;
                    }
                }

                // Rebuild sidebar tree
                await initFolderTree();

                // Re-render sidebar state
                renderFavourites();
                renderMachines();

                // Dispatch event for app.js header update
                window.dispatchEvent(new CustomEvent('machine-changed', {
                    detail: { machine, name: machine.name, source: 'sidebar' }
                }));

                closeMenu();
                closeSidebarMobile();
            } finally {
                switchingMachine = false;
            }
        });
        menu.appendChild(item);
    });
}


